import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  useGetRoleTagsQuery,
  useCreateRoleTagMutation,
  useUpdateRoleTagMutation,
  useDeleteRoleTagMutation,
  useReorderRoleTagsMutation,
  RoleTag,
} from "../../../app/services/roles";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import IconButton from "../../../components/button/IconButton";
import Input from "../../../components/form-item/Input";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

import ConfirmModal from "../../../components/modal/ConfirmModal";
import FullWidthAddBlock from "./FullWidthAddBlock";
import { useStatusIndicator } from "../hooks/useStatusIndicator";

// 関数シグネチャ用の型エイリアス。引数名は型エディタ補完での可読性目的で残し、
// eslint の no-unused-vars 警告のみ抑制している。
/* eslint-disable no-unused-vars -- 関数シグネチャの引数名はドキュメント */
type RenameFn = (tagId: number, name: string) => void;
type RoleTagFn = (tag: RoleTag) => void;
/* eslint-enable no-unused-vars */

/** タグ名のリアルタイム保存のデバウンス時間 (ms)。入力が止まってから保存する。 */
const RENAME_DEBOUNCE_MS = 500;

const SectionTitle = styled.div.attrs({ className: "heading-16" })`
  color: var(--soremo-text);
`;

const HelpText = styled.p.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
`;

const TagListContainer = styled.div.attrs({ className: "u-col u-w100" })`
  gap: 8px;
`;

const TagRow = styled.div.attrs({
  className: "u-row u-w100 u-gap8 u-align-center",
})`
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  padding: 8px 12px;
  background: #fff;
`;

const DragHandle = styled.div`
  flex-shrink: 0;
  color: var(--soremo-light-gray);
  display: flex;
  align-items: center;
  user-select: none;
`;

const NameInputWrapper = styled.div`
  flex: 1;
  min-width: 120px;
`;

const Order = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
  font-family: "din-2014", sans-serif;
  font-weight: 800;
  min-width: 24px;
  text-align: right;
`;

interface SortableTagRowProps {
  tag: RoleTag;
  onCommitRename: RenameFn;
  onRequestDelete: RoleTagFn;
}

/**
 * 1 タグ = 1 行。@dnd-kit/sortable の useSortable で並び替え対応。
 * ドラッグハンドル (`drag_indicator`) のみが listeners をホストし、
 * 名前入力やボタンは通常のクリック操作と競合しないようにしている。
 *
 * 名称はデバウンス自動保存（リアルタイム更新）。サーバ側の最新名を `tag.name`
 * 経由で受け取り、ローカル入力中の値とは独立に同期させる。
 */
const SortableTagRow: React.FC<SortableTagRowProps> = ({
  tag,
  onCommitRename,
  onRequestDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tag.id });

  const [draft, setDraft] = useState(tag.name);

  // サーバ側の最新名で同期（保存成功後・他端末更新時など）
  useEffect(() => {
    setDraft(tag.name);
  }, [tag.name]);

  const debouncedDraft = useDebouncedValue(draft, RENAME_DEBOUNCE_MS);

  useEffect(() => {
    const trimmed = debouncedDraft.trim();
    if (trimmed === "" || trimmed === tag.name) return;
    onCommitRename(tag.id, trimmed);
  }, [debouncedDraft, tag.id, tag.name, onCommitRename]);

  const style: React.CSSProperties = {
    width: "100%",
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <TagRow>
        <DragHandle
          {...attributes}
          {...listeners}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
          aria-label="並び替えハンドル"
        >
          <span className="material-symbols-rounded" style={{ fontSize: 20 }}>
            drag_indicator
          </span>
        </DragHandle>
        <Order>#{tag.order}</Order>
        <NameInputWrapper>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </NameInputWrapper>
        <IconButton onClick={() => onRequestDelete(tag)} icon="delete" />
      </TagRow>
    </div>
  );
};

const RoleTagsManager: React.FC = () => {
  const { data, isLoading } = useGetRoleTagsQuery();
  const [createRoleTag] = useCreateRoleTagMutation();
  const [updateRoleTag] = useUpdateRoleTagMutation();
  const [deleteRoleTag] = useDeleteRoleTagMutation();
  const [reorderRoleTags] = useReorderRoleTagsMutation();

  const sourceTags: RoleTag[] = useMemo(
    () => data?.data?.tags ?? [],
    [data],
  );

  const [localOrder, setLocalOrder] = useState<RoleTag[] | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RoleTag | null>(null);
  const { status, indicate } = useStatusIndicator();

  // サーバ側の最新一覧を受け取ったらローカルの並び替え下書きをクリア
  useEffect(() => {
    setLocalOrder(null);
  }, [sourceTags]);

  const tags = localOrder ?? sourceTags;

  const handleCommitRename = useCallback<RenameFn>(
    async (tagId, name) => {
      try {
        indicate("saving");
        await updateRoleTag({ tagId, payload: { name } }).unwrap();
        indicate("saved");
      } catch (e) {
        console.error("Failed to rename tag:", e);
        indicate("error", 3000);
      }
    },
    [updateRoleTag, indicate],
  );

  const handleAdd = useCallback(async () => {
    // プレースホルダ名で即時 POST し、ユーザーは行の入力欄で名称を確定する。
    // 入力欄はデバウンス自動保存 (`SortableTagRow`) なので別途「保存」操作は不要。
    try {
      indicate("creating");
      await createRoleTag({ name: "新しいタグ" }).unwrap();
      indicate("saved");
    } catch (e) {
      console.error("Failed to create tag:", e);
      indicate("error", 3000);
    }
  }, [createRoleTag, indicate]);

  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const tag = pendingDelete;
    setPendingDelete(null);
    try {
      indicate("deleting");
      await deleteRoleTag(tag.id).unwrap();
      indicate("saved");
    } catch (e) {
      console.error("Failed to delete tag:", e);
      indicate("error", 3000);
    }
  }, [deleteRoleTag, pendingDelete, indicate]);

  // ===== D&D 並び替え =====
  // PointerSensor の activationConstraint で誤発火を抑制（5px ドラッグでアクティブ化）
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const sortableIds = useMemo(() => tags.map((t) => t.id), [tags]);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sortableIds.indexOf(active.id as number);
      const newIndex = sortableIds.indexOf(over.id as number);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(tags, oldIndex, newIndex);
      setLocalOrder(reordered);
      try {
        indicate("saving");
        await reorderRoleTags({
          ordered_ids: reordered.map((t) => t.id),
        }).unwrap();
        indicate("saved");
      } catch (e) {
        console.error("Failed to reorder tags:", e);
        indicate("error", 3000);
        setLocalOrder(null);
      }
    },
    [tags, sortableIds, reorderRoleTags, indicate],
  );

  if (isLoading) {
    return <p className="bodytext-13">読み込み中...</p>;
  }

  return (
    <>
      <div className="u-col u-w100 u-gap16">
        <div className="u-col u-w100 u-gap4">
          <SectionTitle>ロールタグ一覧</SectionTitle>
          <HelpText>
            タグの追加・名称変更・削除・並び替えができます。タグの並び順はドラッグ＆ドロップで変更できます。タグを削除すると、各ロールに付与されたタグも一緒に外れます。
          </HelpText>
        </div>

        {tags.length === 0 ? (
          <HelpText>
            タグがまだ登録されていません。下のブロックから追加してください。
          </HelpText>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              <TagListContainer>
                {tags.map((tag) => (
                  <SortableTagRow
                    key={tag.id}
                    tag={tag}
                    onCommitRename={handleCommitRename}
                    onRequestDelete={setPendingDelete}
                  />
                ))}
              </TagListContainer>
            </SortableContext>
          </DndContext>
        )}

        <FullWidthAddBlock label="タグを追加" onClick={handleAdd} />
      </div>

      <StatusIndicator status={status} withBottomAppBar={false} />

      {pendingDelete && (
        <ConfirmModal
          message={`「${pendingDelete.name}」タグを削除しますか？\n各ロールに付与されたこのタグもまとめて外れます (cascade)。この操作は元に戻せません。`}
          confirmLabel="削除"
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </>
  );
};

export default RoleTagsManager;
